import type {
  DashboardColumnKind,
  DashboardComparisonOperator,
  DashboardFilter,
  DashboardFilterOperator,
  DashboardPresenceOperator,
} from './types';

/**
 * Which comparisons each kind of column offers, and what a filter needs to be
 * finished (PRD - Dashboards, Feature 4 · SL-1665).
 *
 * **The catalogue is data, not code**, the same contract `widgetTypes.ts` holds
 * over the visualisations: the operators a column offers are declared here and
 * read at runtime, and nothing downstream branches on a column's kind. That is
 * what lets a sixth kind land as one row rather than as a `switch` in the
 * section, the well and the card.
 *
 * Ported from the design prototype's `operatorsFor`
 * (`Design/src/pages/analytics/builder/WidgetBuilderDrawer.tsx`), with two
 * deliberate departures:
 *
 * - **Words, not symbols.** The prototype persists `≥`; this stores `gte`. The
 *   identifiers are saved with the dashboard, and a symbol in stored data is a
 *   symbol every consumer has to decode — the label is the panel's job and
 *   lives in i18n, where it can also be translated.
 * - **`choice` is its own kind.** The prototype splits `enum` from `boolean`
 *   and gives them the same operators; here both pick from a closed list, so
 *   what separates them is *where the list comes from* — the column's schema
 *   for a choice, the two constants below for a boolean — not which
 *   comparisons they allow.
 */

/**
 * Offered by every kind, and the only comparisons some columns can honestly
 * make.
 *
 * A `file` column holds a name and a `button` holds nothing at all (both arrive
 * as `text`, see `tableSchemaService`): asking whether they are empty is
 * meaningful, asking what they contain is not always. Listing them last
 * everywhere puts the two that are always available at the foot of the menu,
 * where a reader stops looking for the specific ones.
 */
const PRESENCE: DashboardPresenceOperator[] = ['empty', 'notEmpty'];

/**
 * Comparisons per kind of column, in the order the operator picker draws them.
 *
 * Ordered by how often each is reached for, not alphabetically: `is` before
 * `isNot`, equality before the inequalities.
 */
const COMPARISONS: Record<DashboardColumnKind, DashboardComparisonOperator[]> = {
  text: ['contains', 'is', 'isNot', 'startsWith', 'endsWith'],
  number: ['is', 'isNot', 'gt', 'gte', 'lt', 'lte'],
  // No `contains` on a date, and no `isNot` either: "not on the 3rd" is a
  // question nobody asks of a timestamp, while before/after are the whole point.
  date: ['is', 'gt', 'lt'],
  // A closed list compares by identity. `contains` over an option the user
  // picked from a menu would be comparing a value against itself, loosely.
  choice: ['is', 'isNot'],
  boolean: ['is', 'isNot'],
};

/** The operators a column of this kind offers, presence ones last. */
export const operatorsFor = (kind: DashboardColumnKind): DashboardFilterOperator[] => [
  ...COMPARISONS[kind],
  ...PRESENCE,
];

/**
 * The operator a new filter over this kind opens on.
 *
 * The first comparison of its kind rather than a fixed one: a new filter over
 * text opens on "contains" and over a number on "=", which is what each is
 * reached for. Never a presence operator — a filter that starts on "is empty"
 * is already complete, and one the user has not written yet should not be.
 */
export const defaultOperatorFor = (kind: DashboardColumnKind): DashboardComparisonOperator =>
  COMPARISONS[kind][0];

/**
 * Whether this operator needs a value from the user.
 *
 * The one place that question is answered, so the section, the validation and
 * the operations cannot disagree about whether a filter is finished. Derived
 * from the presence list rather than restated, so adding a third valueless
 * operator is one edit.
 *
 * A **type guard** and not a plain boolean: `DashboardFilter` is discriminated
 * on the operator, so a caller that has asked this question should not have to
 * repeat the answer with a cast when it builds the filter. Narrowing here is
 * what keeps the union honest all the way to the assignment.
 */
export const operatorTakesValue = (
  operator: DashboardFilterOperator,
): operator is DashboardComparisonOperator =>
  !(PRESENCE as DashboardFilterOperator[]).includes(operator);

/**
 * Whether this filter is one that carries a value.
 *
 * The same question as `operatorTakesValue`, asked of the filter rather than of
 * its operator — and it has to be its own guard, because narrowing a property
 * does not narrow the object that holds it: TypeScript cannot know that a
 * `filter` whose `operator` is a comparison is the half of the union with a
 * `value`. Written once here so no caller reaches for a cast.
 */
export const filterTakesValue = (
  filter: DashboardFilter,
): filter is Extract<DashboardFilter, { value: string }> => operatorTakesValue(filter.operator);

/**
 * Whether a filter is finished enough to narrow anything.
 *
 * A filter is born half-made — a column and an operator, no value yet — and
 * that is a normal state rather than an error: the user is still typing. What
 * it must never do is *silently* narrow nothing, which is why the panel says so
 * (see `widgetValidation`) instead of the evaluator quietly skipping it.
 *
 * Whitespace is not a value. A filter whose box holds only spaces reads as
 * written to the eye and matches nothing at all, which is the worst of both.
 */
export const isFilterComplete = (filter: DashboardFilter): boolean =>
  !filterTakesValue(filter) || filter.value.trim() !== '';

/**
 * The values a boolean column is compared against.
 *
 * Declared here and not in the section because they are *stored* strings, like
 * every other filter value: what the panel shows for each is an i18n leaf
 * (`widget.filter.boolean.*`), which is also why these are not `'Sí'`/`'No'`.
 */
export const BOOLEAN_FILTER_VALUES = ['true', 'false'] as const;
